<template>
    <main class="guided-plan-wrapper">
        <!-- Top bar -->
        <div class="guided-topbar">
            <div class="guided-topbar__left">
                <RouterLink
                    :to="`/organizations/${organizationId}/learners/${learnerId}/dashboard`"
                    class="guided-topbar__back"
                >
                    <span class="material-symbols-outlined">arrow_back</span>
                    <span class="guided-topbar__back-text">Learner</span>
                </RouterLink>
                <div class="guided-topbar__title-group">
                    <h1 class="guided-topbar__title">Work Packet</h1>
                </div>
            </div>
            <div class="guided-topbar__right">
                <button
                    type="button"
                    class="guided-topbar__toggle"
                    @click="switchToFullPacket"
                    title="Switch to the Full Packet view with all 12 sections"
                >
                    <span class="material-symbols-outlined">view_list</span>
                    <span class="guided-topbar__toggle-text">Full Packet</span>
                </button>
            </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading && !assessment" class="guided-loading">
            <div class="guided-loading__skeleton guided-loading__skeleton--stepper"></div>
            <div class="guided-loading__skeleton guided-loading__skeleton--content"></div>
        </div>

        <template v-else>
            <!-- Stepper -->
            <GuidedStepper :current-step="currentStep" @select="setStep" />

            <!-- Active screen -->
            <transition name="screen" mode="out-in">
                <div class="guided-content" :key="currentStep">
                    <Overview
                        v-if="currentStep === 1"
                        :organizationId="organizationId"
                        :learnerId="learnerId"
                        :assessmentId="assessmentId"
                        :assessment="assessment"
                        @go-to-step="setStep"
                        @view-full-packet="switchToFullPacket"
                    />
                    <BuildProgram
                        v-else-if="currentStep === 2"
                        :organizationId="organizationId"
                        :learnerId="learnerId"
                        :assessmentId="assessmentId"
                        :assessment="assessment"
                        :canConductAssessment="canConductAssessment"
                    />
                    <GuidedProgramTargets
                        v-else-if="currentStep === 3"
                        :organizationId="organizationId"
                        :learnerId="learnerId"
                        :assessmentId="assessmentId"
                        :assessment="assessment"
                        :canConductAssessment="canConductAssessment"
                    />
                </div>
            </transition>

            <!-- Bottom nav -->
            <div class="guided-bottom-nav">
                <button
                    type="button"
                    class="guided-bottom-nav__btn guided-bottom-nav__btn--secondary"
                    :disabled="currentStep === 1"
                    @click="prevStep"
                >
                    <span class="material-symbols-outlined">arrow_back</span>
                    <span>Previous</span>
                </button>
                <div class="guided-bottom-nav__progress">
                    <span>Step {{ currentStep }} of 3</span>
                </div>
                <button
                    type="button"
                    class="guided-bottom-nav__btn guided-bottom-nav__btn--primary"
                    :disabled="currentStep === 3"
                    @click="nextStep"
                >
                    <span>Next</span>
                    <span class="material-symbols-outlined">arrow_forward</span>
                </button>
            </div>
        </template>
    </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import GuidedStepper from '@/components/assesments/guided/GuidedStepper.vue';
import Overview from '@/components/assesments/guided/Overview.vue';
import BuildProgram from '@/components/assesments/guided/BuildProgram.vue';
import GuidedProgramTargets from '@/components/assesments/guided/GuidedProgramTargets.vue';

const route = useRoute();
const router = useRouter();

const organizationId = ref(Number(route.params.organization ?? 0));
const learnerId = ref(Number(route.params.learner ?? 0));
const assessmentId = ref(Number(route.params.assessment ?? 0));

const currentStep = ref(1);
const assessment = ref(null);
const loading = ref(true);

const canConductAssessment = computed(() => assessment.value?.can_edit);

const fetchAssessment = async () => {
    loading.value = true;
    try {
        const { data } = await api.get(
            `/organizations/${organizationId.value}/assessments/${assessmentId.value}`
        );
        assessment.value = data?.data || null;
    } catch (error) {
        handleError(error);
    } finally {
        loading.value = false;
    }
};

const setStep = (stepId) => {
    if (stepId === currentStep.value) return;
    currentStep.value = stepId;
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

const nextStep = () => {
    if (currentStep.value < 3) setStep(currentStep.value + 1);
};

const prevStep = () => {
    if (currentStep.value > 1) setStep(currentStep.value - 1);
};

const switchToFullPacket = () => {
    router.push({
        name: 'LearnerWorkpacketDetail',
        params: {
            organization: organizationId.value,
            learner: learnerId.value,
            assessment: assessmentId.value,
        },
    });
};

onMounted(fetchAssessment);
</script>

<style scoped>
.guided-plan-wrapper {
    min-height: 100vh;
    background: #f6f5ff;
    padding: 20px 24px 80px;
}

/* Top bar (not sticky — the global site Header is already sticky at z-index:10) */
.guided-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 20px;
    gap: 16px;
    flex-wrap: wrap;
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
    padding: 12px 16px;
}

.guided-topbar__left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.guided-topbar__back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #725CFF;
    font-weight: 500;
    text-decoration: none;
    font-size: 14px;
    padding: 6px 10px;
    border-radius: 8px;
    transition: background 0.15s;
}

.guided-topbar__back:hover {
    background: #ece8ff;
}

.guided-topbar__back .material-symbols-outlined {
    font-size: 18px;
}

.guided-topbar__title-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.guided-topbar__title {
    font-size: 22px;
    font-weight: 700;
    color: #222;
    margin: 0;
}

.guided-topbar__badge {
    background: #725CFF;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
    letter-spacing: 0.5px;
}

.guided-topbar__toggle {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #fff;
    border: 1px solid #725CFF;
    color: #725CFF;
    padding: 8px 14px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    font-size: 13px;
    transition: background 0.15s, color 0.15s;
}

.guided-topbar__toggle:hover {
    background: #725CFF;
    color: #fff;
}

.guided-topbar__toggle .material-symbols-outlined {
    font-size: 18px;
}

/* Loading state */
.guided-loading {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.guided-loading__skeleton {
    background: linear-gradient(90deg, #ededed 25%, #f5f5f5 50%, #ededed 75%);
    background-size: 200% 100%;
    animation: shimmer 1.6s ease-in-out infinite;
    border-radius: 12px;
}

.guided-loading__skeleton--stepper {
    height: 90px;
}

.guided-loading__skeleton--content {
    height: 400px;
}

@keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
}

/* Content */
.guided-content {
    margin-bottom: 24px;
    min-height: 400px;
}

/* Screen transitions */
.screen-enter-active,
.screen-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.screen-enter-from {
    opacity: 0;
    transform: translateY(8px);
}

.screen-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

/* Bottom nav */
.guided-bottom-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    background: #fff;
    border-radius: 12px;
    padding: 12px 16px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.08);
}

.guided-bottom-nav__progress {
    font-size: 13px;
    color: #888;
    font-weight: 500;
}

.guided-bottom-nav__btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 8px;
    font-weight: 500;
    cursor: pointer;
    font-size: 14px;
    border: none;
    transition: transform 0.1s, box-shadow 0.15s;
}

.guided-bottom-nav__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.guided-bottom-nav__btn--primary {
    background: #725CFF;
    color: #fff;
    box-shadow: 0 2px 6px rgba(114, 92, 255, 0.25);
}

.guided-bottom-nav__btn--primary:not(:disabled):hover {
    background: #5d47e0;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(114, 92, 255, 0.35);
}

.guided-bottom-nav__btn--secondary {
    background: #fff;
    color: #725CFF;
    border: 1px solid #e4dfff;
}

.guided-bottom-nav__btn--secondary:not(:disabled):hover {
    background: #f7f6ff;
}

.guided-bottom-nav__btn .material-symbols-outlined {
    font-size: 18px;
}

@media (max-width: 768px) {
    .guided-plan-wrapper {
        padding: 12px 16px 80px;
    }

    .guided-topbar__title {
        font-size: 18px;
    }

    .guided-topbar__back-text {
        display: none;
    }

    .guided-topbar__toggle-text {
        display: none;
    }

    .guided-topbar__toggle {
        padding: 8px 10px;
    }

    .guided-bottom-nav {
        position: sticky;
        bottom: 12px;
    }

    .guided-bottom-nav__btn span:not(.material-symbols-outlined) {
        display: none;
    }

    .guided-bottom-nav__progress {
        font-size: 12px;
    }
}
</style>
